import { Link, useLocation } from "wouter";
import {
  Users,
  FileText,
  Settings,
  Menu,
  X,
  Package,
  Wrench,
  Upload,
  Plug,
  Receipt,
  HelpCircle,
  LogOut,
  ShieldCheck,
  BookOpen,
  AlertTriangle,
  LayoutTemplate,
  FolderKanban,
  CalendarDays,
  LayoutDashboard,
  Activity,
  HardHat,
} from "lucide-react";
import { ZoomControl } from "@/components/zoom-control";
import { Button } from "@/components/ui/button";
import { ReactNode, useState, useEffect, useCallback, useRef } from "react";
import { useAuth, getTrialInfo } from "@/hooks/use-auth";
import { useSupportNotifications } from "@/hooks/use-support-notifications";
import { useSetupGuide } from "@/hooks/use-setup-guide";
import { useFieldMode } from "@/hooks/use-field-mode";
import { useTimezoneBackfill } from "@/hooks/use-timezone-backfill";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

function GracePeriodBanner() {
  const { user } = useAuth();
  const [portalLoading, setPortalLoading] = useState(false);

  const openBillingPortal = useCallback(async () => {
    setPortalLoading(true);
    try {
      const res = await fetch(`${BASE}/api/billing/portal`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (data.url) {
        window.open(data.url, "_blank", "noopener,noreferrer");
      }
    } finally {
      setPortalLoading(false);
    }
  }, []);

  if (!user?.inGracePeriod || !user.gracePeriodEndsAt) return null;

  const endsAt = new Date(user.gracePeriodEndsAt);
  const now = new Date();
  const diffMs = endsAt.getTime() - now.getTime();
  const daysLeft = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));

  return (
    <div style={{
      background: "#fefce8",
      borderBottom: "1px solid #fde047",
      padding: "0.625rem 1rem",
      display: "flex",
      alignItems: "center",
      gap: "0.625rem",
      flexWrap: "wrap",
    }}>
      <AlertTriangle style={{ width: "1rem", height: "1rem", color: "#ca8a04", flexShrink: 0 }} />
      <span style={{ fontSize: "0.875rem", color: "#713f12", flex: 1 }}>
        <strong>Payment failed.</strong> Your Pro access will end in {daysLeft} day{daysLeft !== 1 ? "s" : ""} if the issue isn't resolved. Please update your payment method.
      </span>
      <button
        onClick={openBillingPortal}
        disabled={portalLoading}
        style={{
          fontSize: "0.8125rem",
          fontWeight: 600,
          color: "#713f12",
          background: "#fde047",
          border: "1px solid #ca8a04",
          borderRadius: "0.375rem",
          padding: "0.25rem 0.75rem",
          cursor: portalLoading ? "not-allowed" : "pointer",
          opacity: portalLoading ? 0.7 : 1,
          whiteSpace: "nowrap",
        }}
      >
        {portalLoading ? "Loading…" : "Update Payment Method"}
      </button>
    </div>
  );
}

function TrialBanner() {
  const { user } = useAuth();
  const { isOnTrial, trialDaysLeft } = getTrialInfo(user);

  if (!isOnTrial) return null;

  const urgent = trialDaysLeft <= 2;

  return (
    <div style={{
      background: urgent ? "#fff7ed" : "#f0fdf4",
      borderBottom: `1px solid ${urgent ? "#fed7aa" : "#bbf7d0"}`,
      padding: "0.5rem 1rem",
      display: "flex",
      alignItems: "center",
      gap: "0.625rem",
      flexWrap: "wrap",
    }}>
      <span style={{ fontSize: "0.875rem", color: urgent ? "#9a3412" : "#166534", flex: 1 }}>
        {urgent
          ? <><strong>Your free trial ends {trialDaysLeft === 1 ? "tomorrow" : "in 2 days"}.</strong> Upgrade now to keep access to Pro features.</>
          : <><strong>{trialDaysLeft} days left</strong> in your free Pro trial. Explore everything before it ends.</>
        }
      </span>
      <a
        href="/upgrade"
        style={{
          fontSize: "0.8125rem",
          fontWeight: 600,
          color: urgent ? "#9a3412" : "#166534",
          background: urgent ? "#fed7aa" : "#bbf7d0",
          border: `1px solid ${urgent ? "#fb923c" : "#4ade80"}`,
          borderRadius: "0.375rem",
          padding: "0.25rem 0.75rem",
          textDecoration: "none",
          whiteSpace: "nowrap",
        }}
      >
        Upgrade to Pro
      </a>
    </div>
  );
}

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 768);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    setIsDesktop(mq.matches);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isDesktop;
}

export function Layout({ children }: { children: ReactNode }) {
  const [location] = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const mainRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTop = 0;
    }
  }, [location]);

  // Stamp the sidebar open/closed state on body so GuidedTourOverlay can
  // read it synchronously inside its RAF tick and switch spotlight targets.
  useEffect(() => {
    document.body.dataset.mobileSidebar = mobileOpen ? "open" : "closed";
  }, [mobileOpen]);

  useTimezoneBackfill();

  const isDesktop = useIsDesktop();
  const { user, logout } = useAuth();
  const isField = user?.role === "field";
  const { fieldMode, setFieldMode } = useFieldMode();

  // Field workers and owners in field mode get a minimal full-screen layout.
  if (isField || fieldMode) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        <header style={{
          height: "3.25rem",
          borderBottom: "1px solid hsl(var(--border))",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 1rem",
          background: "hsl(var(--card))",
          position: "sticky",
          top: 0,
          zIndex: 10,
          flexShrink: 0,
        }}>
          <Link href="/field">
            <img src="/logo-new.png" alt="QuoTrak" style={{ height: "2.5rem", width: "auto", objectFit: "contain", cursor: "pointer" }} />
          </Link>
          {user && (
            <div style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
              {!isField && fieldMode && (
                <button
                  onClick={() => setFieldMode(false)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.375rem",
                    fontSize: "0.8125rem",
                    color: "hsl(var(--muted-foreground))",
                    background: "none",
                    border: "1px solid hsl(var(--border))",
                    cursor: "pointer",
                    padding: "0.25rem 0.625rem",
                    borderRadius: "0.375rem",
                  }}
                  className="hover:bg-muted hover:text-foreground transition-colors"
                >
                  ← Full app
                </button>
              )}
              {isField && (
                <Link href="/field/settings">
                  <button
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.375rem",
                      fontSize: "0.875rem",
                      color: "hsl(var(--muted-foreground))",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "0.375rem 0.5rem",
                      borderRadius: "0.375rem",
                    }}
                    className="hover:bg-muted hover:text-foreground transition-colors"
                  >
                    <Settings style={{ width: "1rem", height: "1rem" }} />
                  </button>
                </Link>
              )}
              <button
                onClick={async () => { await logout(); }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.375rem",
                  fontSize: "0.875rem",
                  color: "hsl(var(--muted-foreground))",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "0.375rem 0.5rem",
                  borderRadius: "0.375rem",
                }}
                className="hover:bg-muted hover:text-foreground transition-colors"
              >
                <LogOut style={{ width: "1rem", height: "1rem" }} />
                Sign Out
              </button>
            </div>
          )}
        </header>
        <main ref={mainRef} style={{ flex: 1, padding: "1rem", overflowY: "auto" }}>
          <div style={{ maxWidth: "72rem", margin: "0 auto" }}>
            {children}
          </div>
        </main>
      </div>
    );
  }

  const workspaceNavItems = [
    { href: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { href: "/quotes", label: "Quotes", icon: FileText },
    { href: "/invoices", label: "Invoices", icon: Receipt },
    { href: "/customers", label: "Customers", icon: Users },
  ];

  const projectNavItems = [
    { href: "/projects", label: "Projects", icon: FolderKanban },
    { href: "/calendar", label: "Calendar", icon: CalendarDays },
    { href: "/field", label: "Field", icon: HardHat },
  ];

  const catalogNavItems = [
    { href: "/products", label: "Products & Services", icon: Package },
    { href: "/cost-catalog", label: "Cost Catalog", icon: Wrench },
    { href: "/templates", label: "Templates", icon: LayoutTemplate },
  ];

  const isActive = (href: string) => {
    if (href === "/dashboard") return location === "/dashboard" || location === "/";
    if (href === "/invoices") return location === "/invoices" || location === "/invoices/list";
    // Exact-match /settings so /settings/team doesn't also highlight it
    if (href === "/settings") return location === "/settings";
    return location.startsWith(href);
  };

  const NavLink = ({ href, label, icon: Icon }: { href: string; label: string; icon: React.ElementType }) => (
    <Link href={href}>
      <div
        data-tour-target={`nav-${href}`}
        onClick={() => setMobileOpen(false)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.75rem",
          padding: "0.625rem 0.75rem",
          borderRadius: "0.375rem",
          cursor: "pointer",
          transition: "background 0.15s, color 0.15s, box-shadow 0.15s",
          backgroundColor: isActive(href) ? "hsl(var(--sidebar-accent))" : "transparent",
          color: isActive(href) ? "hsl(var(--sidebar-accent-foreground))" : "hsl(var(--sidebar-foreground) / 0.7)",
          fontWeight: isActive(href) ? 500 : 400,
          boxShadow: isActive(href) ? "inset 3px 0 0 hsl(var(--primary))" : "none",
        }}
      >
        <Icon style={{ width: "1.25rem", height: "1.25rem", flexShrink: 0, color: isActive(href) ? "hsl(var(--primary))" : "inherit" }} />
        {label}
      </div>
    </Link>
  );

  const SidebarContent = ({ onClose }: { onClose?: () => void }) => {
    const { totalUnread: supportUnread } = useSupportNotifications();
    const { guideState, openGuide } = useSetupGuide();

    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <div style={{ padding: "1.25rem 0.75rem 0.5rem" }}>
          <Link href="/dashboard">
            <div
              onClick={onClose}
              style={{ display: "flex", alignItems: "center", padding: "0 0.75rem", marginBottom: "1rem", cursor: "pointer" }}
            >
              <img src="/logo-new.png" alt="QuoTrak" style={{ height: "3.5rem", width: "auto", objectFit: "contain" }} />
            </div>
          </Link>

          <nav style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            {workspaceNavItems.map((item) => <NavLink key={item.href} {...item} />)}
          </nav>

          <div style={{ marginTop: "1rem", paddingTop: "1rem", borderTop: "1px solid hsl(var(--sidebar-border))", display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            <span style={{ fontSize: "0.625rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.09em", color: "hsl(var(--sidebar-foreground) / 0.35)", paddingLeft: "0.75rem", paddingBottom: "0.25rem", display: "block" }}>Projects</span>
            {projectNavItems.map((item) => <NavLink key={item.href} {...item} />)}
          </div>

          <div style={{ marginTop: "1rem", paddingTop: "1rem", borderTop: "1px solid hsl(var(--sidebar-border))", display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            <span style={{ fontSize: "0.625rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.09em", color: "hsl(var(--sidebar-foreground) / 0.35)", paddingLeft: "0.75rem", paddingBottom: "0.25rem", display: "block" }}>Catalog</span>
            {catalogNavItems.map((item) => <NavLink key={item.href} {...item} />)}
          </div>

          <div style={{ marginTop: "1rem", paddingTop: "1rem", borderTop: "1px solid hsl(var(--sidebar-border))" }}>
            <span style={{ fontSize: "0.625rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.09em", color: "hsl(var(--sidebar-foreground) / 0.35)", paddingLeft: "0.75rem", paddingBottom: "0.25rem", display: "block" }}>Tools</span>
            <NavLink href="/integrations" label="Integrations" icon={Plug} />
            <NavLink href="/import" label="Import & Export" icon={Upload} />

            <Link href="/support">
              <div
                onClick={onClose}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  padding: "0.625rem 0.75rem",
                  borderRadius: "0.375rem",
                  cursor: "pointer",
                  backgroundColor: location === "/support" ? "hsl(var(--sidebar-accent))" : "transparent",
                  color: location === "/support" ? "hsl(var(--sidebar-accent-foreground))" : "hsl(var(--sidebar-foreground) / 0.7)",
                  fontWeight: location === "/support" ? 500 : 400,
                  transition: "background 0.15s, color 0.15s, box-shadow 0.15s",
                  boxShadow: location === "/support" ? "inset 3px 0 0 hsl(var(--primary))" : "none",
                  marginBottom: "0.25rem",
                  justifyContent: "space-between",
                }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                  <HelpCircle style={{ width: "1.25rem", height: "1.25rem", flexShrink: 0, color: location === "/support" ? "hsl(var(--primary))" : "inherit" }} />
                  Help & Support
                </span>
                {supportUnread > 0 && (
                  <span style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: "1.25rem",
                    height: "1.25rem",
                    padding: "0 0.25rem",
                    borderRadius: "9999px",
                    background: "#ef4444",
                    color: "#fff",
                    fontSize: "0.6875rem",
                    fontWeight: 700,
                    flexShrink: 0,
                  }}>
                    {supportUnread > 99 ? "99+" : supportUnread}
                  </span>
                )}
              </div>
            </Link>

            {guideState && (() => {
              const resolvedDismissed = guideState.dismissedSteps ?? [];
              const handled = guideState.steps.filter(s => guideState.completedSteps.includes(s) || resolvedDismissed.includes(s)).length;
              const total = guideState.steps.length;
              if (handled === total) return null;
              return (
                <button
                  onClick={() => { openGuide(); onClose?.(); }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    padding: "0.625rem 0.75rem",
                    borderRadius: "0.375rem",
                    cursor: "pointer",
                    background: "none",
                    border: "none",
                    width: "100%",
                    marginBottom: "0.5rem",
                    color: "hsl(var(--sidebar-foreground) / 0.7)",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = "hsl(var(--sidebar-accent) / 0.4)")}
                  onMouseLeave={e => (e.currentTarget.style.background = "none")}
                >
                  <BookOpen style={{ width: "1.25rem", height: "1.25rem", flexShrink: 0, color: "hsl(var(--primary))" }} />
                  <span style={{ flex: 1, textAlign: "left", fontSize: "0.875rem" }}>Setup Guide</span>
                  <span style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: "1.375rem",
                    height: "1.375rem",
                    padding: "0 0.3rem",
                    borderRadius: "9999px",
                    background: "hsl(var(--primary))",
                    color: "hsl(var(--primary-foreground))",
                    fontSize: "0.6875rem",
                    fontWeight: 700,
                    flexShrink: 0,
                  }}>
                    {handled}/{total}
                  </span>
                </button>
              );
            })()}
          </div>

          <div style={{ marginTop: "1rem", paddingTop: "1rem", borderTop: "1px solid hsl(var(--sidebar-border))", display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            <span style={{ fontSize: "0.625rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.09em", color: "hsl(var(--sidebar-foreground) / 0.35)", paddingLeft: "0.75rem", paddingBottom: "0.25rem", display: "block" }}>Account</span>
            <NavLink href="/settings" label="Settings" icon={Settings} />
            <NavLink href="/settings/team" label="Team" icon={HardHat} />
            <NavLink href="/activity" label="Activity" icon={Activity} />
            {user?.isAdmin && <NavLink href="/admin" label="Admin Console" icon={ShieldCheck} />}
          </div>
        </div>

        <div style={{ marginTop: "auto", borderTop: "1px solid hsl(var(--sidebar-border))" }}>
          {user && (
            <div style={{ padding: "1rem 0.75rem 0.5rem", paddingLeft: "1.5rem" }}>
              <p style={{ fontSize: "0.75rem", color: "hsl(var(--sidebar-foreground) / 0.5)", marginBottom: "0.375rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {user.email}
              </p>
              <button
                onClick={async () => { await logout(); }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  fontSize: "0.8125rem",
                  color: "hsl(var(--sidebar-foreground) / 0.6)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "0.375rem 0",
                  width: "100%",
                }}
              >
                <LogOut style={{ width: "1rem", height: "1rem" }} />
                Sign Out
              </button>
            </div>
          )}
          <div style={{ borderTop: "1px solid hsl(var(--sidebar-border))", paddingTop: "0.5rem", paddingBottom: "0.75rem" }}>
            <ZoomControl />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", width: "100%" }}>
      {isDesktop && (
        <aside style={{
          display: "flex",
          flexDirection: "column",
          width: "16rem",
          borderRight: "1px solid hsl(var(--sidebar-border))",
          background: "hsl(var(--sidebar))",
          flexShrink: 0,
          height: "100vh",
          position: "sticky",
          top: 0,
          overflowY: "auto",
        }}>
          <SidebarContent />
        </aside>
      )}

      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <GracePeriodBanner />
        <TrialBanner />
        {!isDesktop && (
          <header style={{
            height: "3.5rem",
            borderBottom: "1px solid hsl(var(--border))",
            display: "flex",
            alignItems: "center",
            padding: "0 1rem",
            justifyContent: "center",
            background: "hsl(var(--card))",
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}>
            <Link href="/dashboard">
              <div style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                <img src="/logo-new.png" alt="QuoTrak" style={{ height: "2.75rem", width: "auto", objectFit: "contain" }} />
              </div>
            </Link>
          </header>
        )}

        {!isDesktop && mobileOpen && (
          <>
            <div
              onClick={() => setMobileOpen(false)}
              style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 40 }}
            />
            <div style={{
              position: "fixed",
              top: 0,
              left: 0,
              bottom: 0,
              width: "16rem",
              background: "hsl(var(--sidebar))",
              borderRight: "1px solid hsl(var(--sidebar-border))",
              zIndex: 50,
              display: "flex",
              flexDirection: "column",
              overflowY: "auto",
            }}>
              <div style={{ display: "flex", justifyContent: "flex-end", padding: "0.75rem" }}>
                <Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)}
                  style={{ color: "hsl(var(--sidebar-foreground) / 0.7)" }}>
                  <X style={{ width: "1.25rem", height: "1.25rem" }} />
                </Button>
              </div>
              <SidebarContent onClose={() => setMobileOpen(false)} />
            </div>
          </>
        )}

        <main ref={mainRef} style={{ flex: 1, padding: isDesktop ? "2rem" : "1rem", overflowY: "auto", paddingBottom: isDesktop ? undefined : "5rem" }}>
          <div style={{ maxWidth: "72rem", margin: "0 auto" }}>
            {children}
          </div>
        </main>

        {/* Mobile menu FAB — bottom-left, thumb-friendly */}
        {!isDesktop && (
          <button
            data-tour-target="mobile-menu-btn"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
            style={{
              position: "fixed",
              bottom: "1.5rem",
              left: "1.5rem",
              zIndex: 30,
              width: "3.25rem",
              height: "3.25rem",
              borderRadius: "9999px",
              background: "hsl(var(--primary))",
              color: "hsl(var(--primary-foreground))",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
            }}
          >
            <Menu style={{ width: "1.375rem", height: "1.375rem" }} />
          </button>
        )}
      </div>
    </div>
  );
}
